import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from '@angular/router';
import { Observable } from 'rxjs';
import { filter, first, switchMap, tap } from 'rxjs/operators';
import { HSPARole } from 'src/app/shared/models/hspa-role.enum';
import { MenuEnum } from '../nav-menu/model/nav-menu.model';
import { UserMenuManagementService } from '../nav-menu/service/user-menu-management.service';
import { AuthService } from './auth.service';
import { AuthorizeService } from './authorize.service';
import { IUser } from 'src/app/shared/models/IUser';

@Injectable()
export class AuthGuardWithForcedLogin {
  private isDefaultUser = false;

  constructor(
    private authService: AuthService,
    private authorizeService: AuthorizeService,
    private router: Router,
    private userMenuManagementService: UserMenuManagementService
  ) {}

  canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.authService.isDoneLoading$
    .pipe(
      filter(isDone => isDone),
      switchMap(() => this.authService.isAuthenticated$),
      tap(isAuthenticated => {
        if (isAuthenticated) {
          this.handleAuthenticatedUser(route, state.url);
        } else {
          this.authService.login(state.url);
        }
      })
    );
  }

  private handleAuthenticatedUser(route: ActivatedRouteSnapshot, returnUrl: string): void {
    this.authorizeService.getUser().pipe(
      first(),
      tap((user: IUser) => {
        this.isDefaultUser = returnUrl === '' || returnUrl === '/';
        this.redirectBasedOnPermissions(route, user);
      })
    ).subscribe();
  }

  private redirectBasedOnPermissions(route: ActivatedRouteSnapshot, user: IUser): void {
    const userPermission = parseInt(user.permission, 10);
    const expectedPermissions = route.data.expectedPermissions;

    if (expectedPermissions !== undefined && (userPermission & expectedPermissions) !== expectedPermissions) {
      this.router.navigate(['/not-authorized']);
      return;
    }

    if (this.isDefaultUser) {
      this.redirectBasedOnRole(user);
    } else if (route.data.isAdminParent) {
      this.userMenuManagementService.setSelectedMenu(MenuEnum.Admin);
    }
  }

  private redirectBasedOnRole(user: IUser): void {
    if (user.roles.includes(HSPARole.BDSystemAdministrator)) {
      this.userMenuManagementService.setSelectedMenu(MenuEnum.Admin);
      this.router.navigate(['/idns']);
    } else if (user.roles.includes(HSPARole.ClinicianUser)) {
      this.userMenuManagementService.setSelectedMenu(MenuEnum.Clinical);
      this.router.navigate(['/clinician']);
    } else if (user.roles.includes(HSPARole.CustomerSystemAdministrator)) {
      this.userMenuManagementService.setSelectedMenu(MenuEnum.CustomerSystemAdministrator);
      this.router.navigate(['/users']);
    }
  }
}